import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Linkedin, ShieldCheck, MessageCircle, Send, Twitter, Globe } from "lucide-react";
import { SITE } from "@/data/site";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import TutorCtaLink from "@/components/TutorCtaLink";
import { LOCALITIES } from "@/data/seoData";

// Map social keys to a Lucide icon
const ICON = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
  twitter: Twitter,
  telegram: Send,
  whatsapp: MessageCircle,
  gbp: Globe,
};

export default function Footer() {
  const s = useSiteSettings();
  const social = s.social || {};
  const enabledSocials = Object.entries(social).filter(([, v]) => v?.enabled && v?.url);

  return (
    <footer className="bg-brand-deep text-white/85 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-brand-radial opacity-90" />
      <div className="relative max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-5 sm:gap-6">
            <div
              className="bg-white rounded-2xl shadow-lg ring-1 ring-white/15 flex items-center justify-center shrink-0 p-3 sm:p-3.5 w-[100px] h-[100px] sm:w-[120px] sm:h-[120px]"
              data-testid="footer-logo-wrap"
            >
              <img
                src={SITE.logo}
                alt="Unique Home Tutors"
                width="800"
                height="674"
                className="uht-logo block max-w-full max-h-full w-auto h-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              <div className="font-display text-lg sm:text-xl font-bold leading-tight" data-testid="footer-business-name">{s.business_name}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest text-brand-cyan mt-1 leading-tight">{s.tagline}</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/70 leading-relaxed">
            Verified home tutors, small-batch coaching and 1:1 live online classes for Nursery to Class 12, all boards,
            NEET / JEE and language &amp; computer courses. Serving Jabalpur, Nagpur, Pan-India and foreign students.
          </p>
          <div className="flex gap-3 mt-5 flex-wrap" data-testid="footer-socials">
            {enabledSocials.map(([key, { url }]) => {
              const Icon = ICON[key] || Globe;
              return (
                <a key={key} href={url} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 grid place-items-center hover:bg-brand-cyan hover:text-brand-deep transition" aria-label={key} data-testid={`social-${key}`}>
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
          <p className="text-xs text-white/60 mt-3">@uniquehometutorsofficial</p>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display text-base text-white mb-4">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/home-tuition" className="hover:text-brand-cyan">Home Tuition</Link></li>
            <li><Link to="/coaching-classes" className="hover:text-brand-cyan">Coaching Classes</Link></li>
            <li><Link to="/online-classes" className="hover:text-brand-cyan">Online Classes</Link></li>
            <li><Link to="/competitive-exams" className="hover:text-brand-cyan">NEET / JEE</Link></li>
            <li><Link to="/spoken-english" className="hover:text-brand-cyan">Spoken English</Link></li>
            <li><Link to="/computer-courses" className="hover:text-brand-cyan">Computer Courses</Link></li>
            <li><Link to="/courses" className="hover:text-brand-cyan">All Courses</Link></li>
            <li><Link to="/boards" className="hover:text-brand-cyan">Boards</Link></li>
            <li><Link to="/grades" className="hover:text-brand-cyan">Grades</Link></li>
            <li><Link to="/blog" className="hover:text-brand-cyan">Blog</Link></li>
            <li><Link to="/book-my-tutor" className="hover:text-brand-cyan" data-testid="footer-bmt-link">Book My Tutor App</Link></li>
            <li><Link to="/about" className="hover:text-brand-cyan">About / Founder</Link></li>
            <li><Link to="/contact" className="hover:text-brand-cyan">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-base text-white mb-4">For Parents &amp; Tutors</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/book-demo" className="hover:text-brand-cyan">Book Free Demo</Link></li>
            <li><TutorCtaLink placement="footer" className="hover:text-brand-cyan" data-testid="cta-tutor-footer">Become a Tutor</TutorCtaLink></li>
            <li><Link to="/career" className="hover:text-brand-cyan">Tutor Jobs / Career</Link></li>
            <li><Link to="/pay-now" className="hover:text-brand-cyan">Pay Now</Link></li>
            <li><Link to="/faq" className="hover:text-brand-cyan">Parent &amp; Tutor FAQs</Link></li>
            <li><Link to="/policies/tutor-policy" className="hover:text-brand-cyan">Tutor Policy</Link></li>
            <li><Link to="/policies/parent-policy" className="hover:text-brand-cyan">Parent Policy</Link></li>
            <li><Link to="/policies/refund" className="hover:text-brand-cyan">Refund Policy</Link></li>
            <li><Link to="/policies/fee-payment" className="hover:text-brand-cyan">Fee &amp; Payment</Link></li>
            <li><Link to="/policies/gap-compensation" className="hover:text-brand-cyan">Gap &amp; Compensation</Link></li>
            <li><Link to="/policies/recontinuation" className="hover:text-brand-cyan">Re-continuation</Link></li>
            <li><Link to="/policies/privacy" className="hover:text-brand-cyan">Privacy</Link></li>
            <li><Link to="/policies/terms" className="hover:text-brand-cyan">Terms</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-base text-white mb-4">Reach Us</h4>
          <div className="text-sm space-y-3">
            <div>
              <div className="text-brand-cyan font-semibold uppercase tracking-wider text-xs">Jabalpur (HO)</div>
              <a
                href={SITE.jabalpur.mapsDirections}
                target="_blank"
                rel="noreferrer"
                className="flex gap-2 mt-1 hover:text-brand-cyan transition group"
                data-testid="footer-ho-address"
                aria-label="Get directions to Jabalpur office on Google Maps"
              >
                <MapPin size={14} className="mt-1 flex-shrink-0 group-hover:text-brand-cyan" />
                <span className="underline-offset-2 group-hover:underline">{s.ho_address}</span>
              </a>
              <p className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${(s.ho_phone || "").replace(/\s+/g, "")}`} className="hover:text-brand-cyan" data-testid="footer-ho-phone">{s.ho_phone}</a></p>
            </div>
            <div>
              <div className="text-brand-cyan font-semibold uppercase tracking-wider text-xs">Nagpur</div>
              <a
                href={SITE.nagpur.mapsDirections}
                target="_blank"
                rel="noreferrer"
                className="flex gap-2 mt-1 hover:text-brand-cyan transition group"
                data-testid="footer-branch-address"
                aria-label="Get directions to Nagpur office on Google Maps"
              >
                <MapPin size={14} className="mt-1 flex-shrink-0 group-hover:text-brand-cyan" />
                <span className="underline-offset-2 group-hover:underline">{s.branch_address}</span>
              </a>
              <p className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${(s.branch_phone || "").replace(/\s+/g, "")}`} className="hover:text-brand-cyan" data-testid="footer-branch-phone">{s.branch_phone}</a></p>
            </div>
            <p className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${s.support_email}`} className="hover:text-brand-cyan" data-testid="footer-email">{s.support_email}</a></p>
          </div>
        </div>
      </div>

      {/* SEO Cities + Localities internal-link bar */}
      <div className="relative border-t border-white/10 bg-brand-deep/80">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h4 className="font-display text-sm text-white/80 mb-4 uppercase tracking-[0.18em]">Home Tuition Across India</h4>
          <div className="grid md:grid-cols-2 gap-6 text-xs">
            <div>
              <div className="text-brand-cyan font-semibold mb-2">Jabalpur Localities</div>
              <div className="flex flex-wrap gap-x-2 gap-y-1.5 text-white/65">
                {LOCALITIES.jabalpur.map((l) => (
                  <Link key={l.slug} to={`/jabalpur/areas/${l.slug}`} className="hover:text-brand-cyan">
                    {l.name}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="text-brand-cyan font-semibold mb-2">Nagpur Localities</div>
              <div className="flex flex-wrap gap-x-2 gap-y-1.5 text-white/65">
                {LOCALITIES.nagpur.map((l) => (
                  <Link key={l.slug} to={`/nagpur/areas/${l.slug}`} className="hover:text-brand-cyan">
                    {l.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-6 text-xs">
            <div>
              <div className="text-brand-cyan font-semibold mb-2">Popular in Jabalpur</div>
              <div className="flex flex-wrap gap-x-2 gap-y-1.5 text-white/65">
                {["home-tuition","home-tutors","online-tuition","neet-coaching","jee-coaching","cbse-tuition","icse-tuition","class-10-tuition","class-12-tuition","mathematics-tuition","physics-tuition","spoken-english-classes"].map((t) => (
                  <Link key={t} to={`/jabalpur/${t}`} className="hover:text-brand-cyan">
                    {t.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="text-brand-cyan font-semibold mb-2">Popular in Nagpur</div>
              <div className="flex flex-wrap gap-x-2 gap-y-1.5 text-white/65">
                {["home-tuition","home-tutors","online-tuition","neet-coaching","jee-coaching","cbse-tuition","icse-tuition","class-10-tuition","class-12-tuition","mathematics-tuition","physics-tuition","spoken-english-classes"].map((t) => (
                  <Link key={t} to={`/nagpur/${t}`} className="hover:text-brand-cyan">
                    {t.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 text-xs">
            <span className="text-brand-cyan font-semibold mr-2">International Online Tuition:</span>
            <span className="text-white/65 inline-flex flex-wrap gap-x-2 gap-y-1">
              {["usa","uk","canada","australia","uae","singapore","saudi-arabia","europe"].map((c) => (
                <Link key={c} to={`/online-tuition/${c}`} className="hover:text-brand-cyan capitalize">
                  {c.replace("-", " ")}
                </Link>
              ))}
            </span>
          </div>
          <div className="mt-6 text-xs">
            <span className="text-brand-cyan font-semibold mr-2">Tutor Jobs:</span>
            <span className="text-white/65 inline-flex flex-wrap gap-x-2 gap-y-1">
              {[
                ["home-tutor-jobs","Home Tutor"],
                ["online-tutor-jobs","Online Tutor"],
                ["part-time-tutor-jobs","Part-Time"],
                ["female-tutor-jobs","Female Tutor"],
                ["math-tutor-jobs","Math Tutor"],
                ["physics-tutor-jobs","Physics"],
                ["chemistry-tutor-jobs","Chemistry"],
                ["biology-tutor-jobs","Biology"],
                ["english-tutor-jobs","English"],
                ["neet-faculty-jobs","NEET Faculty"],
                ["jee-faculty-jobs","JEE Faculty"],
                ["cbse-tutor-jobs","CBSE"],
                ["icse-tutor-jobs","ICSE"],
                ["ib-tutor-jobs","IB"],
              ].map(([slug, label]) => (
                <Link key={slug} to={`/tutor-jobs/${slug}`} className="hover:text-brand-cyan">
                  {label}
                </Link>
              ))}
              <Link to="/tutor-jobs" className="hover:text-brand-cyan font-medium underline">All Tutor Jobs →</Link>
            </span>
          </div>
        </div>
      </div>
      <div className="relative border-t border-white/10 pb-20 sm:pb-5">
        <div className="max-w-7xl mx-auto px-4 py-5 text-xs flex flex-col md:flex-row justify-between gap-3 text-white/60">
          <span data-testid="footer-copyright">{s.footer_copyright || `© ${new Date().getFullYear()} Unique Home Tutors. All rights reserved.`}</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/sitemap" className="hover:text-brand-cyan" data-testid="footer-sitemap">Sitemap</Link>
            <span className="text-white/30">|</span>
            <Link to="/policies/privacy" className="hover:text-brand-cyan" data-testid="footer-privacy">Privacy Policy</Link>
            <span className="text-white/30">|</span>
            <Link to="/policies/terms" className="hover:text-brand-cyan" data-testid="footer-terms">Terms &amp; Conditions</Link>
            {s.show_footer_admin_login && (
              <>
                <span className="text-white/30">|</span>
                <Link
                  to="/admin-login"
                  className="hover:text-brand-cyan inline-flex items-center gap-1"
                  data-testid="footer-admin-login"
                >
                  <ShieldCheck size={11} /> Admin Login
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
